import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Button,
  DataTable,
  DataTableColumnHeader,
  PrimaryFullScreenSpinner,
  Txt,
  toast,
} from "@repo/ui";
import { brandsByKey } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Suspense, useMemo, useState } from "react";
import {
  LogoSelectorDialog,
  type LogoSelectorDialogLogo,
} from "~/components/LogoSelectorDialog";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import type { AdminProvidersQuery } from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/providers")({
  head: () => opsHead("Providers"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const providersQueryDocument = graphql(`
  query AdminProviders {
    providers {
      id
      name
      internalName
      logo {
        id
        slug
        url
      }
    }
  }
`);

const setProviderLogoDocument = graphql(`
  mutation AdminSetProviderLogo($input: SetProviderLogoInput!) {
    setProviderLogo(input: $input) {
      provider {
        id
        logo {
          id
          slug
          url
        }
      }
    }
  }
`);

type ProviderRow = NonNullable<AdminProvidersQuery["providers"]>[number];

function View() {
  const { data } = useSuspenseQuery(providersQueryDocument);
  const [editingProvider, setEditingProvider] = useState<ProviderRow | null>(
    null,
  );
  const [setProviderLogo, { loading: setProviderLogoLoading }] = useMutation(
    setProviderLogoDocument,
  );
  const providers = (data.providers ?? [])
    .slice()
    .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));

  const columns = useMemo<ColumnDef<ProviderRow>[]>(
    () => [
      {
        accessorKey: "name",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Name" />
        ),
      },
      {
        accessorKey: "displayName",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Display Name" />
        ),
        cell: ({ row }) => {
          const bankDisplay = brandsByKey[row.original.name];
          return bankDisplay?.displayName ?? row.original.name;
        },
      },
      {
        accessorKey: "internalName",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Internal Name" />
        ),
        cell: ({ row }) => row.original.internalName ?? "No internal name.",
      },
      {
        id: "logo",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Logo" />
        ),
        cell: ({ row }) => {
          if (!row.original.logo) {
            return <span className="text-secondary text-sm">No logo</span>;
          }

          return (
            <div className="flex items-center gap-3">
              <img
                className="size-10 rounded border bg-white object-contain p-1"
                alt={row.original.logo.slug}
                src={row.original.logo.url}
              />
              <Txt variant="mono">{row.original.logo.slug}</Txt>
            </div>
          );
        },
        enableSorting: false,
      },
      {
        accessorKey: "id",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Provider ID" />
        ),
      },
      {
        id: "actions",
        header: () => <div className="sr-only">Actions</div>,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditingProvider(row.original)}
            >
              Set logo
            </Button>
          </div>
        ),
        enableSorting: false,
      },
    ],
    [],
  );

  return (
    <AdminPageContainer testId="admin-providers">
      <AdminPageHeader
        title="Providers"
        subtitle="These are the providers in Primary's codebase, with which products are associated."
      />
      <DataTable
        data={providers}
        columns={columns}
        className="overflow-hidden border"
      />
      <LogoSelectorDialog
        open={editingProvider !== null}
        title={`Set logo for ${editingProvider?.internalName ?? editingProvider?.name ?? "provider"}`}
        currentLogo={editingProvider?.logo ?? null}
        submitLoading={setProviderLogoLoading}
        onOpenChange={(open) => {
          if (!open) {
            setEditingProvider(null);
          }
        }}
        onSubmit={async (logo: LogoSelectorDialogLogo) => {
          if (editingProvider === null) {
            return;
          }

          await setProviderLogo({
            variables: {
              input: {
                providerId: editingProvider.id,
                logoId: logo.id,
              },
            },
          });

          toast({
            kind: "success",
            title: "Provider logo updated.",
            autoHide: true,
          });
          setEditingProvider(null);
        }}
      />
    </AdminPageContainer>
  );
}
